import { toastError, toastSuccess } from '@components/Toast/Toast';
import { useMutation } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { useBankProfile } from '../contexts/BankProfileContext';
import { useAuth } from './useAuth';

enum ERROR_KEY {
	VALIDATION_FAILED = 'VALIDATION_FAILED',
	GENERAL_ERROR = 'GENERAL_ERROR',
}
export interface CreateStatementParams {
	type?: 'pdf' | 'xml' | 'xlsx' | 'csv';
	accountUuid?: string;
	dateFrom?: string;
	dateTo?: string;
	balanceType?: 'available_balance' | 'balance';
}

const useRequestStatement = () => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();
	const { t } = useTranslation('', { keyPrefix: 'statements' });

	const requestStatement = async ({
		type,
		accountUuid,
		dateFrom,
		dateTo,
		balanceType,
	}: CreateStatementParams) => {
		const response = await axios.post(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile.id}/${accountUuid}/statement`,
			{
				type,
				from: dateFrom,
				to: dateTo,
				balance_type: balanceType,
			},
			{
				headers: { Authorization: session?.tokenized },
			},
		);

		return response.data;
	};

	return useMutation({
		mutationFn: requestStatement,
		meta: { suppressGlobalErrors: true },
		onSuccess: async () => {
			toastSuccess(t('generationStarted'));
		},
		onError: (error: AxiosError<{ error_code?: string; errors: unknown }>) => {
			const code = error?.response?.data?.error_code;

			if (code === ERROR_KEY.VALIDATION_FAILED) {
				const errors = error.response?.data?.errors || {};

				Object.entries(errors).forEach(([field]) => {
					toastError(t([`errors.${field}`, `errors.${ERROR_KEY.GENERAL_ERROR}`]));
				});

				return;
			}

			toastError(t([`errors.${code}`, `errors.${ERROR_KEY.GENERAL_ERROR}`]));
		},
	});
};

export { useRequestStatement };
